// src/engines/useSpeechScore.js
// ══════════════════════════════════════════════════════════════════════════════
// 🎤 GRABAR CONTRA UN TEXTO CONOCIDO Y RECIBIR UNA NOTA.
//
// El hook que usan los cuatro ejercicios del Laboratorio de Voz. Es primo del
// oído en vivo de Spacy (`useLiveTranscription`), pero con un objetivo distinto
// y por eso es otro hook y no una opción del mismo:
//
//   · Spacy pinta texto MIENTRAS hablas. Aquí no se pinta nada: leer tu propia
//     transcripción por debajo del pasaje que estás leyendo distrae justo
//     cuando hace falta concentración.
//   · Spacy devuelve texto. Aquí se devuelve una NOTA, calculada en el servidor
//     contra el texto que tocaba decir.
//
// ── LAS DOS LECCIONES QUE YA COSTARON CARO, HEREDADAS ────────────────────────
// Las dos vienen del oído de Spacy y las dos eran fallos silenciosos:
//
//   1. El Blob se manda TAL CUAL. Convertirlo con `.arrayBuffer()` devuelve una
//      promesa, y trozos consecutivos pueden resolver desordenados: Deepgram
//      recibe un WebM descolocado y no devuelve NADA.
//   2. El PRIMER trozo lleva la cabecera del contenedor. Si se descarta por
//      llegar antes de que abra el socket, el stream entero es indescifrable.
//      Por eso hay cola desde el primer milisegundo.
// ══════════════════════════════════════════════════════════════════════════════

import { useCallback, useRef, useState } from 'react';
// ── EL SILENCIO ES PARTE DEL EJERCICIO ───────────────────────────────────────
// Mientras el microfono esta abierto, los sonidos de interfaz callan. No es una
// preferencia: un "tin" de boton grabado dentro de la respuesta del alumno se
// cuela en la transcripcion y estropea la medicion de pronunciacion, que es lo
// unico que este ejercicio vende.
import { vozEnCurso } from '../audio/fx';

const CHUNK_MS = 250;
// Medido de extremo a extremo contra el servidor: la nota tarda entre 370 ms y
// 3,1 s (un pasaje largo es lo que mas cuesta). Con 6 s la holgura era de x1.9
// y en una red lenta el alumno se quedaba SIN NOTA Y SIN ERROR — la peor
// combinacion, porque parece que la app se colgo.
const CIERRE_MS = 15000;

function urlDelSocket() {
    const token = localStorage.getItem('token') || '';
    const base = import.meta.env.VITE_API_URL || '';
    const cola = `/api/speech-lab/score?token=${encodeURIComponent(token)}`;

    // Las reescrituras de vercel.json no negocian el upgrade a WebSocket, así
    // que en producción hay que ir al backend directo. En desarrollo, Vite sí
    // hace de proxy (`ws: true`).
    if (/^https?:\/\//i.test(base)) {
        return base.replace(/^http/i, 'ws').replace(/\/+$/, '') + cola;
    }
    const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocolo}//${window.location.host}${cola}`;
}

export default function useSpeechScore() {
    const [estado, setEstado] = useState('listo');   // listo | grabando | puntuando
    const [puntuacion, setPuntuacion] = useState(null);
    const [error, setError] = useState('');

    const wsRef = useRef(null);
    const grabadorRef = useRef(null);
    const streamRef = useRef(null);
    const colaRef = useRef([]);
    const abiertoRef = useRef(false);
    const resolverRef = useRef(null);

    const limpiar = useCallback(() => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ya cerrado */ }
        streamRef.current = null;
        grabadorRef.current = null;
        colaRef.current = [];
        abiertoRef.current = false;
        try { wsRef.current?.close(); } catch { /* ya cerrado */ }
        wsRef.current = null;
    }, []);

    const enviar = (ws, blob) => {
        try {
            ws.send(blob);
        } catch (e) {
            console.warn('[laboratorio] no se pudo enviar un trozo:', e);
        }
    };

    // ── EMPEZAR ───────────────────────────────────────────────────────────────
    // `config` = { modo, objetivo, nivel?, distractor?, duracion_modelo? }
    const start = useCallback(async (config) => {
        setError('');
        setPuntuacion(null);
        colaRef.current = [];
        abiertoRef.current = false;

        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            setError('No se pudo acceder al micrófono.');
            return false;
        }
        streamRef.current = stream;

        const grabador = new MediaRecorder(stream);
        grabadorRef.current = grabador;

        grabador.ondataavailable = (evento) => {
            if (!evento.data || evento.data.size === 0) return;
            const ws = wsRef.current;
            if (abiertoRef.current && ws && ws.readyState === WebSocket.OPEN) {
                enviar(ws, evento.data);
            } else {
                // Todavía no hay socket. Se guarda: el primero lleva la cabecera.
                colaRef.current.push(evento.data);
            }
        };

        grabador.start(CHUNK_MS);
        vozEnCurso(true);
        setEstado('grabando');

        let ws;
        try {
            ws = new WebSocket(urlDelSocket());
        } catch {
            setError('No se pudo conectar con el evaluador.');
            limpiar();
            vozEnCurso(false);
        setEstado('listo');
            return false;
        }
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => {
            // La configuración va por el canal ya abierto, no en la URL: un
            // pasaje en la URL acaba en los logs del servidor y del proxy.
            try { ws.send(JSON.stringify(config)); } catch { /* se cerró */ }
            abiertoRef.current = true;
            const pendientes = colaRef.current;
            colaRef.current = [];
            for (const trozo of pendientes) enviar(ws, trozo);
        };

        ws.onmessage = (evento) => {
            let dato;
            try { dato = JSON.parse(evento.data); } catch { return; }
            if (dato.tipo === 'resultado') {
                resolverRef.current?.(dato);
            } else if (dato.tipo === 'error') {
                setError(dato.mensaje || 'El evaluador rechazó el ejercicio.');
                resolverRef.current?.(null);
            }
        };

        ws.onclose = (ev) => {
            abiertoRef.current = false;
            // Si el alumno estaba esperando su nota, se desbloquea: mejor un
            // mensaje que una rueda girando para siempre.
            if (resolverRef.current) {
                if (ev.code === 4003) setError('Tu plan no permite este ejercicio ahora.');
                else if (ev.code === 4008) setError('Grabación demasiado larga.');
                else if (ev.code === 4001) setError('Tu sesión caducó. Vuelve a entrar.');
                resolverRef.current(null);
            }
        };

        return true;
    }, [limpiar]);

    // ── SOLTAR Y RECIBIR LA NOTA ──────────────────────────────────────────────
    const stop = useCallback(async () => {
        vozEnCurso(false);
        setEstado('puntuando');

        // ⚠️ HAY QUE ESPERAR A QUE EL GRABADOR TERMINE DE SOLTAR.
        //
        // `requestData()` y `stop()` entregan sus trozos por EVENTO, no al
        // momento. Antes se mandaba "stop" en la linea siguiente, asi que el
        // servidor cerraba el stream de Deepgram y los ultimos trozos llegaban
        // a una puerta cerrada: se perdia la ultima palabra, que en un ejercicio
        // de pronunciacion suele ser justo la que se estaba evaluando.
        //
        // Ahora se espera al evento `onstop`, que el navegador dispara DESPUES
        // de haber entregado todo. Con tope, por si algun navegador no lo manda.
        const grabador = grabadorRef.current;
        if (grabador && grabador.state !== 'inactive') {
            await new Promise((listo) => {
                let hecho = false;
                const acabar = () => { if (!hecho) { hecho = true; listo(); } };
                grabador.onstop = acabar;
                setTimeout(acabar, 600);
                try { grabador.requestData(); } catch { /* no en todos */ }
                try { grabador.stop(); } catch { acabar(); }
            });
        }

        const ws = wsRef.current;
        let resultado = null;

        if (abiertoRef.current && ws && ws.readyState === WebSocket.OPEN) {
            resultado = await new Promise((resolve) => {
                resolverRef.current = resolve;
                setTimeout(() => resolve(null), CIERRE_MS);
                try { ws.send('stop'); } catch { resolve(null); }
            });
            resolverRef.current = null;
        }

        limpiar();
        vozEnCurso(false);
        setEstado('listo');

        if (resultado?.puntuacion) {
            setPuntuacion(resultado.puntuacion);
            return resultado.puntuacion;
        }
        if (resultado && !resultado.puntuacion) {
            setError(resultado.mensaje || 'No se te oyó lo suficiente. Inténtalo otra vez.');
        } else if (!resultado) {
            // Sin resultado Y sin error puesto, el alumno se queda mirando una
            // tarjeta que no cambia. Pase lo que pase, se le dice algo.
            setError((e) => e || 'No llegó tu resultado. Vuelve a intentarlo.');
        }
        return null;
    }, [limpiar]);

    const cancel = useCallback(() => {
        const grabador = grabadorRef.current;
        if (grabador && grabador.state !== 'inactive') {
            try { grabador.stop(); } catch { /* ya parado */ }
        }
        limpiar();
        vozEnCurso(false);
        setEstado('listo');
    }, [limpiar]);

    // Si el alumno sale de la pantalla con el microfono abierto, la señal se
    // quedaba encendida el RESTO DE LA SESION y toda la app enmudecia — sin dar
    // ningun error, asi que parecia que el sonido no estaba implementado.
    useEffect(() => () => { vozEnCurso(false); }, []);

    return { estado, puntuacion, error, start, stop, cancel, setError };
}
